import { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import FilterIndicatorBox from '@/components/ReportDashboard/Shared/FilterIndicatorBox'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { getHeader } from '@/utils/factorAnalytics.util'
import { ReportItem } from '@/constants/ReportDashboard/report-items'
import {
  useAttributionDateRange,
  useAttributionLoading,
  useAttributionPortfolioName,
  useAttributionSecurityContribution,
  useAttributionSecurityError,
} from '@/contexts/FAAttributionDataContext/hooks'
import {
  AttributionMetric,
  ContributorCount,
  METRIC_LABELS,
  ShowContributors,
  useAttributionHoldingsControls,
  useAttributionTopBottom,
} from '@/contexts/AttributionHoldingsContext'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { IGfpaSecurityContrib } from '@/interfaces/gfpaReport.interface'
import { CardProps } from '@/interfaces/raDashboard.interface'
import { getGridThemeClass } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { CellClassParams, ColDef, GridApi, GridReadyEvent, ModuleRegistry, ValueFormatterParams } from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import BarChartIcon from '@mui/icons-material/BarChart'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

ModuleRegistry.registerModules([ClientSideRowModelModule, CsvExportModule, ExcelExportModule, ClipboardModule, RangeSelectionModule, MenuModule])

// Raw, full-precision cell values for copy/export (bypass display formatters).
const clipboardCellValue = (p: any) => {
  const field = p.column?.getColDef?.()?.field
  return field && p.node?.data && p.node.data[field] !== undefined ? p.node.data[field] : p.value
}

const SHOW_OPTIONS: { label: string; value: ShowContributors }[] = [
  { label: 'Top & Bottom', value: 'topBottom' },
  { label: 'Top Only', value: 'topOnly' },
  { label: 'Bottom Only', value: 'bottomOnly' },
]
const COUNT_OPTIONS: ContributorCount[] = [5, 10, 15]

// Metric columns: 2dp, unit lives in the header.
const num2Fmt = (p: ValueFormatterParams) => (p.value == null ? '' : Number(p.value).toFixed(2))
// Market value: full number, thousands-separated, 2dp (CCY).
const mvFmt = (p: ValueFormatterParams) =>
  p.value == null ? '' : Number(p.value).toLocaleString(navigator.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ── ag-grid sub-table for one set (top or bottom) ──
interface SecurityTableProps {
  data: IGfpaSecurityContrib[]
  isTop: boolean
  metric: AttributionMetric
  onApiReady?: (api: GridApi) => void
}

const SecurityTable = ({ data, isTop, metric, onApiReady }: SecurityTableProps) => {
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  // Top contributors read positive (green); bottom read negative (red).
  const accentColor = isTop ? COMPANY_COLORS.SUCCESS : COMPANY_COLORS.ERROR
  const apiRef = useRef<GridApi | null>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => apiRef.current)
  const gridContextMenuItems = useMemo(() => buildCopyContextMenuItems({ processCell: clipboardCellValue }), [])

  const metricCellStyle = (active: boolean) => (_p: CellClassParams) =>
    active ? { color: accentColor, fontWeight: 600 } : { color: theme.textPrimary, fontWeight: 400 }

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { headerName: getHeader(ReportItem.StandardID), field: ReportItem.StandardID, flex: 1, minWidth: 110 },
      { headerName: getHeader(ReportItem.Description), field: ReportItem.Description, flex: 2, minWidth: 150 },
      { headerName: getHeader(ReportItem.MarketValue), field: ReportItem.MarketValue, flex: 1, minWidth: 160, cellClass: 'ag-right-aligned-cell', valueFormatter: mvFmt },
      {
        headerName: 'Security Return (%)',
        field: 'security_ret',
        flex: 1,
        minWidth: 130,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: num2Fmt,
        cellStyle: metricCellStyle(metric === 'securityReturn'),
      },
      {
        headerName: 'Sec Ret Cont (bps)',
        field: 'security_ret_contrib',
        flex: 1,
        minWidth: 150,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: num2Fmt,
        cellStyle: metricCellStyle(metric === 'contribution'),
      },
    ],
    [theme, metric, accentColor],
  )

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: true, filter: false, resizable: true }), [])

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      apiRef.current = e.api
      onApiReady?.(e.api)
    },
    [onApiReady],
  )

  return (
    <Box
      ref={gridBoxRef}
      className={getGridThemeClass(isDarkMode)}
      sx={[
        {
          width: '100%',
          '--ag-secondary-foreground-color': theme.textSecondary,
          '--ag-row-hover-color': getColorWithOpacity(theme.border, 0.4),
          '--ag-cell-horizontal-padding': '10px',
        } as React.CSSProperties,
        gridHeaderIconsSx,
      ]}
    >
      <AgGridReact
        rowData={data}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout='autoHeight'
        onGridReady={onGridReady}
        animateRows={false}
        columnHoverHighlight
        enableRangeSelection
        popupParent={document.body}
        getContextMenuItems={gridContextMenuItems}
        processCellForClipboard={clipboardCellValue}
      />
    </Box>
  )
}

// Top/Bottom contributors card: ranks the attribution securities by contribution or security return.
const TopBottomContributionCard: React.FC<CardProps> = () => {
  const theme = useTheme()
  const rows = useAttributionSecurityContribution()
  const loading = useAttributionLoading()
  const error = useAttributionSecurityError()
  const portfolioName = useAttributionPortfolioName()
  const dateRange = useAttributionDateRange()

  const { metric, setMetric, showContributors, setShowContributors, count, setCount, securitiesFiltered, highlightGroups } = useAttributionHoldingsControls()
  const [viewOptionsAnchor, setViewOptionsAnchor] = useState<null | HTMLElement>(null)

  const topApiRef = useRef<GridApi | null>(null)
  const bottomApiRef = useRef<GridApi | null>(null)
  const setTopApi = useCallback((api: GridApi) => { topApiRef.current = api }, [])
  const setBottomApi = useCallback((api: GridApi) => { bottomApiRef.current = api }, [])

  const { topItems, bottomItems } = useAttributionTopBottom()

  const showTop = showContributors === 'topBottom' || showContributors === 'topOnly'
  const showBottom = showContributors === 'topBottom' || showContributors === 'bottomOnly'

  const cardTitle = useMemo(() => {
    const label = METRIC_LABELS[metric]
    if (showContributors === 'topOnly') return `Top ${count} by ${label}`
    if (showContributors === 'bottomOnly') return `Bottom ${count} by ${label}`
    return `Top ${count} / Bottom ${count} by ${label}`
  }, [metric, showContributors, count])

  const exportFileBase = useMemo(() => {
    const name = (portfolioName?.trim() || 'portfolio').replace(/[\\/:*?"<>|]/g, '_')
    const range = dateRange ? `_${dateRange.beginDate.format('YYYYMMDD')}_${dateRange.endDate.format('YYYYMMDD')}` : ''
    return `${name}_top_bottom${range}`
  }, [portfolioName, dateRange])

  const downloadCsv = (fileName: string, content: string) => {
    const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    setViewOptionsAnchor(null)
    const tApi = showTop && topItems.length > 0 ? topApiRef.current : null
    const bApi = showBottom && bottomItems.length > 0 ? bottomApiRef.current : null
    if (type === 'EXCEL') {
      const sheets = [
        tApi?.getSheetDataForExcel({ sheetName: 'Top', processCellCallback: clipboardCellValue }),
        bApi?.getSheetDataForExcel({ sheetName: 'Bottom', processCellCallback: clipboardCellValue }),
      ].filter((s): s is string => !!s)
      const api = tApi ?? bApi
      if (api && sheets.length) api.exportMultipleSheetsAsExcel({ data: sheets, fileName: `${exportFileBase}.xlsx` })
    } else {
      const parts: string[] = []
      if (tApi) parts.push(`Top ${count} Contributors\n` + tApi.getDataAsCsv({ processCellCallback: clipboardCellValue }))
      if (bApi) parts.push(`Bottom ${count} Contributors\n` + bApi.getDataAsCsv({ processCellCallback: clipboardCellValue }))
      if (parts.length) downloadCsv(`${exportFileBase}.csv`, parts.join('\n\n'))
    }
  }

  const toggleMetric = () => setMetric(metric === 'contribution' ? 'securityReturn' : 'contribution')

  const menuPaperSx = { backgroundColor: theme.cardBackground, border: `1px solid ${theme.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 180 }
  const menuItemSx = { fontSize: '14px', color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: '8px', py: '6px', px: 2 }
  const sectionLabelSx = { px: 2, pt: 1, pb: 0.5, fontSize: '14px', fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }

  const MetricIcon = metric === 'contribution' ? TrendingUpIcon : BarChartIcon

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      {/* Header */}
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: '12px' }}>
        <Stack>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.textPrimary }}>
            {cardTitle} {securitiesFiltered ? <FilterIndicatorBox groups={highlightGroups} /> : null}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>Top and bottom contributors to portfolio return</Typography>
        </Stack>
        <Stack direction='row' alignItems='center' spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
          <Box
            onClick={toggleMetric}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              fontSize: '14px',
              color: theme.textPrimary,
              cursor: 'pointer',
              backgroundColor: theme.cardBackground,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: theme.border },
            }}
          >
            <MetricIcon sx={{ fontSize: 14 }} />
            {METRIC_LABELS[metric]}
          </Box>
          <IconButton size='small' onClick={(e) => setViewOptionsAnchor(e.currentTarget)} sx={{ color: theme.textSecondary, padding: '4px' }}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* View options + export menu */}
      <Menu anchorEl={viewOptionsAnchor} open={Boolean(viewOptionsAnchor)} onClose={() => setViewOptionsAnchor(null)} PaperProps={{ sx: menuPaperSx }}>
        <Typography sx={{ ...sectionLabelSx, color: theme.textPrimary, fontWeight: 700, textTransform: 'none' }}>View Options</Typography>
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <Typography sx={sectionLabelSx}>Show Contributors</Typography>
        {SHOW_OPTIONS.map(({ label, value }) => (
          <MenuItem key={value} onClick={() => { setShowContributors(value); setViewOptionsAnchor(null) }} sx={menuItemSx}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: showContributors === value ? theme.textPrimary : 'transparent', flexShrink: 0 }} />
            {label}
          </MenuItem>
        ))}
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <Typography sx={sectionLabelSx}>Number of Contributors</Typography>
        {COUNT_OPTIONS.map((c) => (
          <MenuItem key={c} onClick={() => { setCount(c); setViewOptionsAnchor(null) }} sx={menuItemSx}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: count === c ? theme.textPrimary : 'transparent', flexShrink: 0 }} />
            {c} Contributors
          </MenuItem>
        ))}
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <Typography sx={sectionLabelSx}>Export</Typography>
        <MenuItem onClick={() => handleExport('EXCEL')} sx={menuItemSx}>Export as Excel</MenuItem>
        <MenuItem onClick={() => handleExport('CSV')} sx={menuItemSx}>Export as CSV</MenuItem>
      </Menu>

      {error ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COMPANY_COLORS.ERROR }}>Couldn&apos;t load holdings</Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary, mt: '4px' }}>There was an error fetching the data. Please retry, or reach out to support if it persists.</Typography>
        </Stack>
      ) : loading && (rows == null || rows.length === 0) ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: COMPANY_COLORS.DARK_BLUE }} />
        </Stack>
      ) : (rows == null || rows.length === 0) ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>Run analysis for the selected portfolio and date range to view holdings.</Typography>
        </Stack>
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {showTop && (
            <>
              <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: '6px' }}>
                <TrendingUpIcon sx={{ fontSize: 14, color: COMPANY_COLORS.SUCCESS }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>Top {count} Contributors</Typography>
              </Stack>
              <Box sx={{ border: `1px solid ${theme.border}`, borderRadius: '8px', mb: showBottom ? '16px' : 0, overflow: 'hidden' }}>
                <SecurityTable data={topItems} isTop metric={metric} onApiReady={setTopApi} />
              </Box>
            </>
          )}
          {showBottom && (
            <>
              <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: '6px' }}>
                <TrendingDownIcon sx={{ fontSize: 14, color: COMPANY_COLORS.ERROR }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: theme.textPrimary }}>Bottom {count} Contributors</Typography>
              </Stack>
              <Box sx={{ border: `1px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                <SecurityTable data={bottomItems} isTop={false} metric={metric} onApiReady={setBottomApi} />
              </Box>
            </>
          )}
        </Box>
      )}
    </Stack>
  )
}

export default TopBottomContributionCard
